/**
 * Rejilla de planificación tipo hoja de cálculo.
 *
 * - Columnas = semanas del año (~52) con virtualización horizontal manual y
 *   columna de descripción fija (sticky left).
 * - Filas virtualizadas verticalmente (altura uniforme): formato (colapsable)
 *   → artículo (editable) → materiales hoja (read-only con Disp).
 * - Edición inline con recálculo instantáneo en el motor cliente.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import type { PlanArticulo, PlanFeed } from '@/types'
import { matchesItemType, type ItemType } from '@/utils/itemType'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'
import {
  PlanEngine,
  PlanMatrix,
  ItemKey,
  keyOfArticulo,
  fmtMiles,
  groupByFamiliaFormato,
  SIN_FAMILIA,
} from '@/utils/planProduccion/engine'

const COL_W = 96
const DESC_W = 360
const ROW_H = 38
const ROW_H_COMPACT = 30
const HEADER_H = 44
const OVERSCAN = 4

type GridRow =
  // Nivel 1 (banda superior): familia. Clave de expansión = nombre de familia.
  | { kind: 'familia'; familia: string; articulos: PlanArticulo[]; leafSet: ItemKey[] }
  // Nivel 2 (banda anidada): formato. Clave de expansión = `familia|formato`.
  | { kind: 'formato'; familia: string; formato: string; fmtKey: string; articulos: PlanArticulo[]; leafSet: ItemKey[] }
  | { kind: 'articulo'; art: PlanArticulo; aKey: ItemKey }
  | { kind: 'serie'; aKey: ItemKey; serie: 'demanda' | 'baseline' | 'realizado' }
  | { kind: 'material'; leafKey: ItemKey; reqUnit: number; isSemi: boolean; aKey: ItemKey; pathKey: string }
  // Desplegable de un semi: inventario propio + componentes de su BOM
  | { kind: 'semiInv'; semiKey: ItemKey; depth: number }
  | {
      kind: 'semiChild'
      childKey: ItemKey
      qtyPerSemi: number      // consumo por unidad de SEMI (BOM clásico)
      qtyPerArticle: number   // equivalente por unidad de artículo terminado
      parentSemi: ItemKey
      pathKey: string
      depth: number
      isSemi: boolean
    }

interface PlanGridProps {
  dark: boolean
  feed: PlanFeed
  engine: PlanEngine
  plan: PlanMatrix
  search: string
  /** Filtro de familia (me_inventfamily). '' = todas. */
  familia: string
  /** Filtro por tipo de artículo (prefijo Q/P/X). Vacío = todos. */
  tipoArticulo: Set<ItemType>
  /** Modo AVP (artículos Químicas/Armaf en lugar de Q/P/X). */
  avp: boolean
  /** Artículos cuya capacidad bajó por el último editado → material(es) en rojo. */
  conflicts: Map<string, string[]>
  /** Descripción del artículo que provocó los conflictos (para el tooltip). */
  conflictCausa: string
  expandedFormatos: Record<string, boolean>
  expandedFamilias: Record<string, boolean>
  expandedArticulos: Record<string, boolean>
  expandedSemis: Record<string, boolean>
  onToggleFormato: (formato: string) => void
  onToggleFamilia: (famKey: string) => void
  onToggleArticulo: (aKey: string) => void
  onToggleSemi: (pathKey: string) => void
  onSetCell: (aKey: string, weekIdx: number, qty: number | null) => void
  onSuggestMax: (aKey: string, weekIdx: number) => void
  /** Alterna si un material limita (bloqueante) o no la producción. */
  onToggleBlocking: (key: ItemKey) => void
  /** Semana seleccionada (drawer abierto) → columna resaltada. */
  selectedWeekIdx: number | null
  /** Clic en cabecera de semana (global) o en celda de material (contextual). */
  onSelectWeek: (weekIdx: number, materialKey?: string) => void
  /** ¿El plan+en curso de ese formato/semana excede el tope configurado? */
  capExceeded: ((formato: string, w: number) => boolean) | null
  /** Densidad compacta: filas de 30px en vez de 38px. */
  compact: boolean
}

// ── Celda editable de plan ────────────────────────────────────────────────────

interface PlanCellProps {
  dark: boolean
  value: number | undefined
  red: boolean
  /** Tope de producción semanal excedido → aviso ámbar (no bloquea). */
  over?: boolean
  placeholder?: string
  suggestLabel: string
  onCommit: (qty: number | null) => void
  onMax: () => void
}

function parseQty(text: string): number | null {
  const clean = text.replace(/[.\s]/g, '').replace(',', '.')
  if (clean === '') return null
  const n = Number(clean)
  return Number.isFinite(n) ? Math.max(0, Math.round(n)) : null
}

function PlanCell({ dark, value, red, over, placeholder, suggestLabel, onCommit, onMax }: PlanCellProps) {
  const [focused, setFocused] = useState(false)
  const [text, setText] = useState('')

  const display = focused ? text : value !== undefined ? fmtMiles(value) : ''

  return (
    <div className="relative group h-full w-full">
      <input
        type="text"
        inputMode="numeric"
        value={display}
        onFocus={(e) => {
          setFocused(true)
          setText(value !== undefined ? String(value) : '')
          e.target.select()
        }}
        onBlur={() => setFocused(false)}
        onChange={(e) => {
          setText(e.target.value)
          onCommit(parseQty(e.target.value))
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
        }}
        className={clsx(
          'h-full w-full text-right text-[11px] font-semibold tabular-nums pr-1.5 pl-5 outline-none border rounded-md transition-colors placeholder:font-medium',
          dark
            ? 'bg-slate-900 border-white/10 text-slate-100 placeholder:text-slate-600 focus:border-sky-500'
            : 'bg-white border-slate-200 text-slate-800 placeholder:text-slate-300 focus:border-sky-400',
          red && 'ring-1 ring-red-500/70',
          !red && over && 'ring-1 ring-amber-400/80',
        )}
        placeholder={placeholder}
      />
      <button
        tabIndex={-1}
        onMouseDown={(e) => { e.preventDefault(); onMax() }}
        title={suggestLabel}
        className={clsx(
          'absolute left-0.5 top-1/2 -translate-y-1/2 hidden group-hover:flex group-focus-within:flex items-center justify-center w-3.5 h-4 rounded-sm text-[10px] leading-none font-bold cursor-pointer transition-colors',
          dark ? 'text-sky-400 hover:bg-sky-500/25' : 'text-sky-500 hover:bg-sky-100',
        )}
      >
        ↥
      </button>
    </div>
  )
}

// ── Rejilla ───────────────────────────────────────────────────────────────────

export function PlanGrid(props: PlanGridProps) {
  const {
    dark, feed, engine, plan, search, familia, tipoArticulo, avp, conflicts, conflictCausa,
    expandedFormatos, expandedFamilias, expandedArticulos,
    onToggleFormato, onToggleFamilia, onToggleArticulo, onSetCell, onSuggestMax,
  } = props
  const { t } = useTranslation()
  const fmtArt = useFmtArticulo()
  const numWeeks = feed.horizonte.length
  const { onToggleBlocking, expandedSemis, onToggleSemi, selectedWeekIdx, onSelectWeek, capExceeded, compact } = props
  const rowH = compact ? ROW_H_COMPACT : ROW_H

  // ── Filas visibles según búsqueda + expansión ──────────────────────────────
  const rows = useMemo<GridRow[]>(() => {
    const q = search.trim().toLowerCase()
    const articulos = q
      ? feed.articulosPlanificables.filter(
          (a) =>
            a.itemid.toLowerCase().includes(q) ||
            a.descripcion.toLowerCase().includes(q) ||
            a.formato.toLowerCase().includes(q) ||
            // …o cualquier componente de su BOM (itemid o descripción)
            engine.componentMatches(keyOfArticulo(a), q),
        )
      : feed.articulosPlanificables
    const articulosFam = familia
      ? articulos.filter((a) => (a.meInventfamily?.trim() || SIN_FAMILIA) === familia)
      : articulos

    const articulosTipo = tipoArticulo.size > 0
      ? articulosFam.filter(
          (a) =>
            matchesItemType(a.itemid, tipoArticulo, avp) ||
            engine.subtreeMaterialsOf(keyOfArticulo(a)).some((m) =>
              matchesItemType(m.key.split('|')[0], tipoArticulo, avp),
            ),
        )
      : articulosFam

    const leavesOfList = (arts: PlanArticulo[]) => {
      const s = new Set<ItemKey>()
      for (const a of arts) for (const l of engine.leavesOf(keyOfArticulo(a))) s.add(l.leafKey)
      return [...s]
    }

    const out: GridRow[] = []
    for (const g of groupByFamiliaFormato(articulosTipo)) {
      out.push({ kind: 'familia', familia: g.familia, articulos: g.articulos, leafSet: leavesOfList(g.articulos) })
      if (!expandedFamilias[g.familia]) continue
      for (const fmt of g.formatos) {
        const fmtKey = `${g.familia}|${fmt.formato}`
        out.push({ kind: 'formato', familia: g.familia, formato: fmt.formato, fmtKey, articulos: fmt.articulos, leafSet: leavesOfList(fmt.articulos) })
        if (!expandedFormatos[fmtKey]) continue
        for (const a of fmt.articulos) {
          const aKey = keyOfArticulo(a)
          out.push({ kind: 'articulo', art: a, aKey })
          // Filas read-only: demanda (pedidos), OPs en curso y producción real.
          if (engine.hasDemanda(aKey)) out.push({ kind: 'serie', aKey, serie: 'demanda' })
          if (engine.hasBaseline(aKey)) out.push({ kind: 'serie', aKey, serie: 'baseline' })
          if (engine.hasRealizado(aKey)) out.push({ kind: 'serie', aKey, serie: 'realizado' })
          if (!expandedArticulos[aKey]) continue
          // Desplegable recursivo del semi: fila de inventario + componentes de su BOM
          const pushSemiExpansion = (semiKey: ItemKey, semiPath: string, semiReqPerArticle: number, depth: number) => {
            out.push({ kind: 'semiInv', semiKey, depth })
            for (const c of engine.childrenOfSemi(semiKey)) {
              const childPath = `${semiPath}>${c.key}`
              out.push({
                kind: 'semiChild',
                childKey: c.key,
                qtyPerSemi: c.qty,
                qtyPerArticle: c.qty * semiReqPerArticle,
                parentSemi: semiKey,
                pathKey: childPath,
                depth,
                isSemi: c.isSemi,
              })
              if (c.isSemi && expandedSemis[childPath]) {
                pushSemiExpansion(c.key, childPath, c.qty * semiReqPerArticle, depth + 1)
              }
            }
          }
          // Nivel 4 = SOLO los hijos directos del artículo final (no el subárbol
          // aplanado). Cada componente cuelga de su padre directo real; los
          // componentes de un semielaborado solo aparecen al desplegar el semi.
          for (const c of engine.directChildrenOf(aKey)) {
            const pathKey = `${aKey}>${c.key}`
            out.push({ kind: 'material', leafKey: c.key, reqUnit: c.qty, isSemi: c.isSemi, aKey, pathKey })
            if (c.isSemi && expandedSemis[pathKey]) pushSemiExpansion(c.key, pathKey, c.qty, 1)
          }
        }
      }
    }
    return out
  }, [feed, engine, search, familia, tipoArticulo, avp, expandedFormatos, expandedFamilias, expandedArticulos, expandedSemis])

  // ── Virtualización (scroll horizontal y vertical) ──────────────────────────
  const containerRef = useRef<HTMLDivElement>(null)
  const initialScrollKeyRef = useRef('')
  const [view, setView] = useState({ left: 0, top: 0, width: 1200, height: 600 })
  const rafRef = useRef(0)

  const onScroll = useCallback(() => {
    cancelAnimationFrame(rafRef.current)
    rafRef.current = requestAnimationFrame(() => {
      const el = containerRef.current
      if (!el) return
      setView({ left: el.scrollLeft, top: el.scrollTop, width: el.clientWidth, height: el.clientHeight })
    })
  }, [])

  useEffect(() => {
    const el = containerRef.current
    if (el) setView({ left: el.scrollLeft, top: el.scrollTop, width: el.clientWidth, height: el.clientHeight })
    return () => cancelAnimationFrame(rafRef.current)
  }, [])

  // Al desplegar/colapsar (cambia `rows`), el alto del contenedor cambia pero no
  // se dispara ningún scroll: re-medimos para que la ventana virtual de filas se
  // recalcule al instante (antes las filas nuevas no aparecían hasta hacer scroll).
  useEffect(() => {
    const el = containerRef.current
    if (el) setView({ left: el.scrollLeft, top: el.scrollTop, width: el.clientWidth, height: el.clientHeight })
  }, [rows])

  const wFrom = Math.max(0, Math.floor(view.left / COL_W) - OVERSCAN)
  const wTo = Math.min(numWeeks, Math.ceil((view.left + view.width - DESC_W) / COL_W) + OVERSCAN)
  const rFrom = Math.max(0, Math.floor(view.top / rowH) - OVERSCAN)
  const rTo = Math.min(rows.length, Math.ceil((view.top + view.height - HEADER_H) / rowH) + OVERSCAN)

  const weeks = feed.horizonte.slice(wFrom, wTo)
  const totalW = DESC_W + numWeeks * COL_W
  const spacerL = wFrom * COL_W
  const spacerR = (numWeeks - wTo) * COL_W

  // Sombra de la columna fija al hacer scroll horizontal (sensación de capa)
  const stickyShadow =
    view.left > 2
      ? dark
        ? 'shadow-[8px_0_14px_-8px_rgba(0,0,0,0.65)]'
        : 'shadow-[8px_0_14px_-8px_rgba(15,23,42,0.18)]'
      : ''

  // Guías verticales de indentación (jerarquía artículo → semi → componente)
  const guides = (levels: number) =>
    Array.from({ length: levels }, (_, gi) => (
      <span
        key={gi}
        className={clsx('absolute top-0 bottom-0 w-px pointer-events-none', dark ? 'bg-white/8' : 'bg-slate-200')}
        style={{ left: 26 + gi * 14 }}
      />
    ))

  // ── Semana actual (resaltado de columna) ───────────────────────────────────
  const todayIdx = useMemo(() => {
    const now = new Date()
    const iso = now.toISOString().slice(0, 10)
    return feed.horizonte.findIndex((s) => s.fechaInicio <= iso && iso <= s.fechaFin)
  }, [feed])

  useEffect(() => {
    const key = `${feed.empresa}-${feed.anio}-${numWeeks}`
    const el = containerRef.current
    if (!el || initialScrollKeyRef.current === key) return
    initialScrollKeyRef.current = key
    if (todayIdx < 0) return

    const left = Math.max(0, todayIdx * COL_W - COL_W)
    el.scrollLeft = left
    setView({ left, top: el.scrollTop, width: el.clientWidth, height: el.clientHeight })
  }, [feed, numWeeks, todayIdx])

  // ── Estilos base ───────────────────────────────────────────────────────────
  const borderCls = dark ? 'border-white/8' : 'border-slate-200'
  const stickyBg = dark ? 'bg-slate-950' : 'bg-white'
  const headBg = dark
    ? 'bg-gradient-to-b from-slate-900 to-slate-900/85 backdrop-blur-md'
    : 'bg-gradient-to-b from-slate-50 to-slate-100/85 backdrop-blur-md'
  const formatoBg = dark ? 'bg-slate-900' : 'bg-slate-100'
  const formatoText = dark ? 'text-slate-100' : 'text-slate-800'
  const familiaBg = dark ? 'bg-slate-900/55' : 'bg-slate-50'
  const familiaText = dark ? 'text-slate-200' : 'text-slate-700'

  const cellWeekCls = (w: number) =>
    clsx(
      'shrink-0 h-full flex items-center justify-end px-1.5 border-r text-[11px] tabular-nums transition-colors',
      borderCls,
      wFrom + w === todayIdx && (dark ? 'bg-sky-500/8' : 'bg-sky-50/70'),
      wFrom + w === selectedWeekIdx && (dark ? 'bg-indigo-500/10' : 'bg-indigo-50/80'),
    )

  function dispColor(leafKey: ItemKey, w: number): string {
    const saldo = engine.saldoFinAt(leafKey, w)
    if (saldo < -1e-9) {
      return engine.isBlocking(leafKey)
        ? 'text-red-500 font-bold'
        : dark ? 'text-amber-400/90' : 'text-amber-600'
    }
    return dark ? 'text-emerald-400/80' : 'text-emerald-600'
  }

  /** Tooltip de celda de material: desglose del saldo de la semana. */
  function matCellTip(key: ItemKey, w: number): string {
    const d = engine.weekDetail(key, w)
    return t('planFormato.matCellTooltip', {
      inicio: fmtMiles(engine.dispInicio(key, w)),
      entradas: fmtMiles(d.entradas + d.salidasOp),
      comprometidos: fmtMiles(d.comprometidos),
      demanda: fmtMiles(engine.leafDemandAt(key, w)),
      fin: fmtMiles(engine.saldoFinAt(key, w)),
    })
  }

  /** Celdas semanales agregadas de una banda (formato o familia): suma de plan,
   *  OPs en curso (baseline) y conteo de roturas, con resaltado de semana/tope. */
  const bandWeekCells = (arts: PlanArticulo[], leafSet: ItemKey[], formato: string, numberCls: string) =>
    weeks.map((_, w) => {
      const wi = wFrom + w
      let fab = 0
      let curso = 0
      for (const a of arts) {
        const k = keyOfArticulo(a)
        fab += plan[k]?.[wi] ?? 0
        curso += engine.baselineAt(k, wi)
      }
      const reds = engine.redCount(leafSet, wi)
      // El tope es por formato; en la banda de familia (formato='') no aplica.
      const overCap = formato ? (capExceeded?.(formato, wi) ?? false) : false
      return (
        <div
          key={w}
          className={clsx(
            'shrink-0 h-full flex flex-col items-end justify-center px-1.5 border-r',
            borderCls,
            wi === todayIdx && (dark ? 'bg-sky-500/10' : 'bg-sky-50'),
            wi === selectedWeekIdx && (dark ? 'bg-indigo-500/10' : 'bg-indigo-50/80'),
            overCap && (dark ? 'bg-amber-500/10' : 'bg-amber-50'),
          )}
          style={{ width: COL_W }}
        >
          <div className="flex items-center gap-1 leading-none">
            {reds > 0 && (
              <span title={t('planFormato.roturaTooltip', { count: reds })} className="text-[10px] text-red-500 cursor-help">⚠</span>
            )}
            {overCap && (
              <span title={t('planFormato.topeExcedido')} className="text-[10px] text-amber-500 cursor-help">▲</span>
            )}
            <span className={clsx('text-[11px] font-bold tabular-nums', overCap ? 'text-amber-500' : numberCls)}>
              {fab > 0 ? fmtMiles(fab) : ''}
            </span>
          </div>
          {curso > 0 && (
            <span
              className={clsx('text-[9px] tabular-nums leading-none mt-0.5', dark ? 'text-slate-500' : 'text-slate-400')}
              title={t('planFormato.enCursoTooltip')}
            >
              ·{fmtMiles(curso)}
            </span>
          )}
        </div>
      )
    })

  return (
    <div
      ref={containerRef}
      onScroll={onScroll}
      className={clsx('overflow-auto rounded-b-xl border border-t-0', borderCls, dark ? 'bg-slate-950' : 'bg-white')}
      style={{ maxHeight: 'calc(100vh - 320px)' }}
    >
      <div style={{ width: totalW, minWidth: '100%' }}>
        {/* ── Cabecera semanas (sticky top) ──────────────────────────────── */}
        <div
          className={clsx('flex sticky top-0 z-20 border-b', borderCls, headBg)}
          style={{ height: HEADER_H }}
        >
          <div
            className={clsx(stickyShadow, 'sticky left-0 z-30 shrink-0 flex items-end px-3 pb-1.5 border-r text-[10px] font-bold uppercase tracking-wider', borderCls, headBg, dark ? 'text-slate-400' : 'text-slate-500')}
            style={{ width: DESC_W }}
          >
            {t('planFormato.colDescripcion')}
          </div>
          <div className="shrink-0" style={{ width: spacerL }} />
          {weeks.map((s, i) => (
            <div
              key={s.etiqueta}
              onClick={() => onSelectWeek(wFrom + i)}
              className={clsx(
                'shrink-0 flex flex-col items-center justify-center border-r cursor-pointer transition-colors',
                borderCls,
                wFrom + i === todayIdx && (dark ? 'bg-sky-500/15' : 'bg-sky-100'),
                wFrom + i === selectedWeekIdx
                  ? dark ? 'bg-indigo-500/25 ring-1 ring-inset ring-indigo-400/50' : 'bg-indigo-100 ring-1 ring-inset ring-indigo-300'
                  : dark ? 'hover:bg-white/5' : 'hover:bg-slate-100',
              )}
              style={{ width: COL_W }}
              title={`${s.fechaInicio} → ${s.fechaFin}\n${t('planFormato.wdAbrir')}`}
            >
              <span className={clsx('text-[11px] font-bold', dark ? 'text-slate-200' : 'text-slate-700')}>
                {s.etiqueta}
              </span>
              <span className={clsx('text-[9px]', dark ? 'text-slate-500' : 'text-slate-400')}>
                {s.fechaInicio.slice(8, 10)}/{s.fechaInicio.slice(5, 7)}
              </span>
            </div>
          ))}
          <div className="shrink-0" style={{ width: spacerR }} />
        </div>

        {/* ── Filas (virtualizadas) ──────────────────────────────────────── */}
        <div style={{ height: rFrom * rowH }} />
        {rows.slice(rFrom, rTo).map((row, i) => {
          const key = `${rFrom + i}`
          if (row.kind === 'familia') {
            const expanded = !!expandedFamilias[row.familia]
            return (
              <div
                key={key}
                className={clsx('flex border-b cursor-pointer select-none', borderCls, formatoBg)}
                style={{ height: rowH }}
                onClick={() => onToggleFamilia(row.familia)}
              >
                <div
                  className={clsx(stickyShadow, 'relative sticky left-0 z-10 shrink-0 flex items-center gap-2 px-3 border-r border-l-2 text-[11px] font-bold', borderCls, formatoBg, formatoText, dark ? 'border-l-sky-500/60' : 'border-l-sky-400')}
                  style={{ width: DESC_W }}
                >
                  <span className={clsx('text-[9px]', dark ? 'text-slate-400' : 'text-slate-500')}>{expanded ? '▼' : '▶'}</span>
                  <span className="truncate uppercase tracking-wide">{row.familia}</span>
                  <span className={clsx('font-semibold text-[9px] rounded-full px-1.5 py-px', dark ? 'bg-white/10 text-slate-400' : 'bg-slate-200 text-slate-500')}>{row.articulos.length}</span>
                </div>
                <div className="shrink-0" style={{ width: spacerL }} />
                {bandWeekCells(row.articulos, row.leafSet, '', formatoText)}
                <div className="shrink-0" style={{ width: spacerR }} />
              </div>
            )
          }

          if (row.kind === 'formato') {
            const expanded = !!expandedFormatos[row.fmtKey]
            return (
              <div
                key={key}
                className={clsx('flex border-b cursor-pointer select-none', borderCls, familiaBg)}
                style={{ height: rowH }}
                onClick={() => onToggleFormato(row.fmtKey)}
              >
                <div
                  className={clsx(stickyShadow, 'relative sticky left-0 z-10 shrink-0 flex items-center gap-2 pl-7 pr-3 border-r border-l-2 text-[11px] font-semibold', borderCls, familiaBg, familiaText, dark ? 'border-l-indigo-500/50' : 'border-l-indigo-300')}
                  style={{ width: DESC_W }}
                >
                  <span className={clsx('text-[9px]', dark ? 'text-slate-400' : 'text-slate-500')}>{expanded ? '▼' : '▶'}</span>
                  <span className="truncate uppercase tracking-wide">{row.formato}</span>
                  <span className={clsx('font-semibold text-[9px] rounded-full px-1.5 py-px', dark ? 'bg-white/10 text-slate-400' : 'bg-slate-200 text-slate-500')}>{row.articulos.length}</span>
                </div>
                <div className="shrink-0" style={{ width: spacerL }} />
                {bandWeekCells(row.articulos, row.leafSet, row.formato, familiaText)}
                <div className="shrink-0" style={{ width: spacerR }} />
              </div>
            )
          }

          if (row.kind === 'articulo') {
            const expanded = !!expandedArticulos[row.aKey]
            const leaves = engine.leavesOf(row.aKey)
            const conflict = conflicts.get(row.aKey)
            return (
              <div
                key={key}
                className={clsx('group/row flex border-b transition-colors', borderCls, dark ? 'bg-sky-500/5 hover:bg-sky-500/10' : 'bg-sky-50/50 hover:bg-sky-100/60')}
                style={{ height: rowH }}
              >
                <div
                  className={clsx(stickyShadow, 'relative sticky left-0 z-10 shrink-0 flex items-center gap-1.5 pl-5 pr-2 border-r cursor-pointer transition-colors', borderCls, dark ? 'bg-slate-950 group-hover/row:bg-slate-900' : 'bg-sky-50 group-hover/row:bg-sky-100')}
                  style={{ width: DESC_W }}
                  onClick={() => onToggleArticulo(row.aKey)}
                  title={`${row.art.descripcion}\n${fmtArt(row.art.itemid)}${row.art.color ? ' · ' + row.art.color : ''}${row.art.estilo ? ' · ' + row.art.estilo : ''}`}
                >
                  <span className={clsx('text-[8px]', dark ? 'text-slate-500' : 'text-slate-400')}>
                    {expanded ? '▼' : '▶'}
                  </span>
                  <div className="min-w-0 leading-tight">
                    <div className={clsx('text-[11px] font-semibold truncate', dark ? 'text-slate-100' : 'text-slate-800')}>
                      {row.art.descripcion}
                    </div>
                    <div className={clsx('text-[9px] truncate', dark ? 'text-slate-500' : 'text-slate-400')}>
                      {fmtArt(row.art.itemid)}
                      {row.art.color && ` · ${row.art.color}`}
                      {row.art.estilo && ` · ${row.art.estilo}`}
                      <span className="ml-1">({leaves.length} {t('planFormato.materiales')})</span>
                    </div>
                  </div>
                  {conflict && (
                    <span
                      title={t('planFormato.conflictoTooltip', {
                        causa: conflictCausa || '—',
                        materiales: conflict.join(', '),
                      })}
                      className={clsx(
                        'shrink-0 text-[8px] font-bold uppercase rounded px-1 py-0.5 cursor-help',
                        dark ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-100 text-amber-700',
                      )}
                    >
                      ⚠ {t('planFormato.conflictoBadge')}
                    </span>
                  )}
                </div>
                <div className="shrink-0" style={{ width: spacerL }} />
                {weeks.map((_, w) => {
                  const wi = wFrom + w
                  let red = false
                  for (const l of leaves) {
                    if (engine.isRed(l.leafKey, wi)) { red = true; break }
                  }
                  const value = plan[row.aKey]?.[wi]
                  const info =
                    value === undefined
                      ? engine.maxProducibleInfo(row.aKey, wi, plan)
                      : { max: Infinity, limitKey: null }
                  // Placeholder uniforme: máx real si hay límite de material; '—' si es ilimitado.
                  const placeholder =
                    value !== undefined ? undefined : Number.isFinite(info.max) ? fmtMiles(info.max) : '—'
                  const limitDesc = info.limitKey
                    ? engine.materialByKey.get(info.limitKey)?.descripcion ?? info.limitKey.split('|')[0]
                    : ''
                  const overCap = capExceeded?.(row.art.formato, wi) ?? false
                  const cellTitle = [
                    limitDesc ? t('planFormato.limitadoPor', { material: limitDesc }) : '',
                    overCap ? t('planFormato.topeExcedido') : '',
                  ].filter(Boolean).join('\n')
                  return (
                    <div
                      key={w}
                      className={clsx(cellWeekCls(w), 'py-1 px-1')}
                      style={{ width: COL_W }}
                      title={cellTitle || undefined}
                    >
                      <PlanCell
                        dark={dark}
                        value={value}
                        red={red}
                        over={overCap}
                        placeholder={placeholder}
                        suggestLabel={t('planFormato.sugerirMax')}
                        onCommit={(qty) => onSetCell(row.aKey, wi, qty)}
                        onMax={() => onSuggestMax(row.aKey, wi)}
                      />
                    </div>
                  )
                })}
                <div className="shrink-0" style={{ width: spacerR }} />
              </div>
            )
          }

          if (row.kind === 'serie') {
            const serieCfg = {
              demanda: {
                label: t('planFormato.demandaRow'),
                tooltip: t('planFormato.demandaTooltip'),
                at: (wi: number) => engine.demandaAt(row.aKey, wi),
                text: dark ? 'text-violet-300' : 'text-violet-600',
                bg: dark ? 'bg-violet-500/[0.04]' : 'bg-violet-50/40',
              },
              baseline: {
                label: t('planFormato.enCursoRow'),
                tooltip: t('planFormato.enCursoTooltip'),
                at: (wi: number) => engine.baselineAt(row.aKey, wi),
                text: dark ? 'text-slate-400' : 'text-slate-500',
                bg: dark ? 'bg-sky-500/[0.03]' : 'bg-sky-50/40',
              },
              realizado: {
                label: t('planFormato.realizadoRow'),
                tooltip: t('planFormato.realizadoTooltip'),
                at: (wi: number) => engine.realizadoAt(row.aKey, wi),
                text: dark ? 'text-emerald-400/90' : 'text-emerald-600',
                bg: dark ? 'bg-emerald-500/[0.03]' : 'bg-emerald-50/40',
              },
            }[row.serie]
            return (
              <div
                key={key}
                className={clsx('plan-row-enter flex border-b', borderCls, serieCfg.bg)}
                style={{ height: rowH }}
              >
                <div
                  className={clsx(stickyShadow, 'relative sticky left-0 z-10 shrink-0 flex items-center gap-1.5 pl-9 pr-2 border-r', borderCls, stickyBg)}
                  style={{ width: DESC_W }}
                  title={serieCfg.tooltip}
                >
                  {guides(1)}
                  <span className={clsx('text-[9px] font-bold uppercase tracking-wide', serieCfg.text)}>
                    ↳ {serieCfg.label}
                  </span>
                </div>
                <div className="shrink-0" style={{ width: spacerL }} />
                {weeks.map((_, w) => {
                  const wi = wFrom + w
                  const q = serieCfg.at(wi)
                  return (
                    <div key={w} className={cellWeekCls(w)} style={{ width: COL_W }}>
                      <span className={clsx('text-[10px] font-medium tabular-nums', serieCfg.text)}>
                        {q > 0 ? fmtMiles(q) : ''}
                      </span>
                    </div>
                  )
                })}
                <div className="shrink-0" style={{ width: spacerR }} />
              </div>
            )
          }

          if (row.kind === 'semiInv') {
            const invMat = engine.materialByKey.get(row.semiKey)
            const stock = invMat?.stock
            const stockTip = stock
              ? t('planFormato.stockAlmacenes', {
                  gava: fmtMiles(stock.gava), vid: fmtMiles(stock.vid),
                  rmt: fmtMiles(stock.rmt), rmtDea: fmtMiles(stock.rmtDea),
                })
              : ''
            return (
              <div key={key} className={clsx('plan-row-enter flex border-b', borderCls, dark ? 'bg-indigo-500/[0.04]' : 'bg-indigo-50/40')} style={{ height: rowH }}>
                <div
                  className={clsx(stickyShadow, 'relative sticky left-0 z-10 shrink-0 flex items-center gap-1.5 pr-2 border-r', borderCls, stickyBg)}
                  style={{ width: DESC_W, paddingLeft: 52 + row.depth * 14 }}
                  title={stockTip}
                >
                  {guides(row.depth + 1)}
                  <span className={clsx('text-[9px] font-bold uppercase tracking-wide', dark ? 'text-indigo-300' : 'text-indigo-600')}>
                    ↳ {t('planFormato.semiInvRow')}
                  </span>
                  <span className={clsx('text-[9px] tabular-nums', dark ? 'text-slate-400' : 'text-slate-500')}>
                    · stock {fmtMiles(stock?.total ?? 0)}
                  </span>
                </div>
                <div className="shrink-0" style={{ width: spacerL }} />
                {weeks.map((_, w) => {
                  const wi = wFrom + w
                  const net = engine.inflowAt(row.semiKey, wi) - engine.committedAt(row.semiKey, wi)
                  const d = engine.weekDetail(row.semiKey, wi)
                  const tip = t('planFormato.invFlujoTooltip', {
                    entradas: fmtMiles(d.entradas), salidas: fmtMiles(d.salidasOp), comprometidos: fmtMiles(d.comprometidos),
                  })
                  return (
                    <div key={w} className={cellWeekCls(w)} style={{ width: COL_W }} title={tip}>
                      <span className={clsx(
                        'text-[10px] font-medium tabular-nums',
                        net > 0 ? (dark ? 'text-emerald-400/80' : 'text-emerald-600')
                          : net < 0 ? 'text-red-400'
                          : dark ? 'text-slate-600' : 'text-slate-300',
                      )}>
                        {net !== 0 ? `${net > 0 ? '+' : ''}${fmtMiles(net)}` : ''}
                      </span>
                    </div>
                  )
                })}
                <div className="shrink-0" style={{ width: spacerR }} />
              </div>
            )
          }

          if (row.kind === 'semiChild') {
            const cMat = engine.materialByKey.get(row.childKey)
            const [cItem, cColor, cEstilo] = row.childKey.split('|')
            const expanded = !!expandedSemis[row.pathKey]
            const qtyFmt = (q: number) => (q % 1 === 0 ? String(q) : q.toFixed(4))
            return (
              <div key={key} className={clsx('plan-row-enter group/row flex border-b transition-colors', borderCls, dark ? 'bg-indigo-500/[0.02] hover:bg-white/[0.03]' : 'bg-indigo-50/20 hover:bg-slate-50')} style={{ height: rowH }}>
                <div
                  className={clsx(
                    'sticky left-0 z-10 shrink-0 flex items-center gap-1.5 pr-2 border-r transition-colors', borderCls, stickyBg,
                    dark ? 'group-hover/row:bg-slate-900' : 'group-hover/row:bg-slate-50',
                    row.isSemi && 'cursor-pointer',
                  )}
                  style={{ width: DESC_W, paddingLeft: 52 + row.depth * 14 }}
                  title={`${cMat?.descripcion ?? fmtArt(cItem)}\n${t('planFormato.porUnidadArticulo', { qty: qtyFmt(row.qtyPerArticle) })}`}
                  onClick={row.isSemi ? () => onToggleSemi(row.pathKey) : undefined}
                >
                  {guides(row.depth + 1)}
                  {row.isSemi && (
                    <span className={clsx('text-[8px]', dark ? 'text-slate-500' : 'text-slate-400')}>
                      {expanded ? '▼' : '▶'}
                    </span>
                  )}
                  <div className="min-w-0 leading-tight flex-1">
                    <div className={clsx('text-[10px] truncate', dark ? 'text-slate-300' : 'text-slate-600')}>
                      {cMat?.descripcion ?? fmtArt(cItem)}
                    </div>
                    <div className={clsx('text-[9px] truncate', dark ? 'text-slate-600' : 'text-slate-400')}>
                      {fmtArt(cItem)}
                      {cColor && ` · ${cColor}`}
                      {cEstilo && ` · ${cEstilo}`}
                      <span className="ml-1">× {qtyFmt(row.qtyPerSemi)} {t('planFormato.porSemi')}</span>
                    </div>
                  </div>
                  {row.isSemi && (
                    <span
                      title={t('planFormato.semiTooltip')}
                      className={clsx('shrink-0 text-[8px] font-bold uppercase rounded px-1 py-0.5', dark ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-600')}
                    >
                      {t('planFormato.semiBadge')}
                    </span>
                  )}
                </div>
                <div className="shrink-0" style={{ width: spacerL }} />
                {weeks.map((_, w) => {
                  const wi = wFrom + w
                  const fin = engine.saldoFinAt(row.childKey, wi)
                  const red = engine.isRed(row.childKey, wi)
                  const consumo = engine.semiShortAt(row.parentSemi, wi) * row.qtyPerSemi
                  const tip = [
                    consumo > 0 ? t('planFormato.consumoViaSemi', { qty: fmtMiles(consumo) }) : '',
                    matCellTip(row.childKey, wi),
                  ].filter(Boolean).join('\n')
                  return (
                    <div
                      key={w}
                      onClick={() => onSelectWeek(wi, row.childKey)}
                      className={clsx(cellWeekCls(w), 'cursor-pointer', red && (dark ? 'bg-red-500/15' : 'bg-red-50'))}
                      style={{ width: COL_W }}
                      title={tip}
                    >
                      <span className={clsx('text-[10px]', dispColor(row.childKey, wi))}>
                        {fmtMiles(fin)}
                      </span>
                    </div>
                  )
                })}
                <div className="shrink-0" style={{ width: spacerR }} />
              </div>
            )
          }

          // material (semi u hoja)
          const mat = engine.materialByKey.get(row.leafKey)
          const [mItem, mColor, mEstilo] = row.leafKey.split('|')
          const blocking = engine.isBlocking(row.leafKey)
          const semiExpanded = row.isSemi && !!expandedSemis[row.pathKey]
          return (
            <div
              key={key}
              className={clsx(
                'plan-row-enter group/row flex border-b transition-colors',
                borderCls,
                (rFrom + i) % 2 === 1 && (dark ? 'bg-white/[0.015]' : 'bg-slate-50/60'),
                dark ? 'hover:bg-white/[0.03]' : 'hover:bg-slate-50',
              )}
              style={{ height: rowH }}
            >
              <div
                className={clsx(
                  'sticky left-0 z-10 shrink-0 flex items-center gap-1.5 pl-9 pr-2 border-r transition-colors', borderCls, stickyBg,
                  dark ? 'group-hover/row:bg-slate-900' : 'group-hover/row:bg-slate-50',
                  row.isSemi && 'cursor-pointer',
                )}
                style={{ width: DESC_W }}
                title={`${mat?.descripcion ?? fmtArt(mItem)}\nreq/ud: ${row.reqUnit}`}
                onClick={row.isSemi ? () => onToggleSemi(row.pathKey) : undefined}
              >
                {row.isSemi && (
                  <span className={clsx('text-[8px]', dark ? 'text-slate-500' : 'text-slate-400')}>
                    {semiExpanded ? '▼' : '▶'}
                  </span>
                )}
                {guides(1)}
                <div className="min-w-0 leading-tight flex-1">
                  <div className={clsx('text-[10px] truncate', dark ? 'text-slate-300' : 'text-slate-600')}>
                    {mat?.descripcion ?? fmtArt(mItem)}
                  </div>
                  <div className={clsx('text-[9px] truncate', dark ? 'text-slate-600' : 'text-slate-400')}>
                    {fmtArt(mItem)}
                    {mColor && ` · ${mColor}`}
                    {mEstilo && ` · ${mEstilo}`}
                    <span className="ml-1">× {row.reqUnit % 1 === 0 ? row.reqUnit : row.reqUnit.toFixed(4)}</span>
                  </div>
                </div>
                {row.isSemi && (
                  <span
                    title={t('planFormato.semiTooltip')}
                    className={clsx('shrink-0 text-[8px] font-bold uppercase rounded px-1 py-0.5', dark ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-600')}
                  >
                    {t('planFormato.semiBadge')}
                  </span>
                )}
                {!row.isSemi && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onToggleBlocking(row.leafKey) }}
                    title={blocking ? t('planFormato.bloqueanteToggleOff') : t('planFormato.bloqueanteToggleOn')}
                    className={clsx(
                      'shrink-0 text-[8px] font-bold uppercase rounded px-1 py-0.5 cursor-pointer transition-colors',
                      blocking
                        ? dark
                          ? 'bg-red-500/15 text-red-300 hover:bg-red-500/30'
                          : 'bg-red-50 text-red-500 hover:bg-red-100'
                        : dark
                          ? 'bg-white/10 text-slate-400 hover:bg-white/20'
                          : 'bg-slate-100 text-slate-400 hover:bg-slate-200',
                    )}
                  >
                    {blocking ? t('planFormato.bloqueanteBadge') : 'NB'}
                  </button>
                )}
              </div>
              <div className="shrink-0" style={{ width: spacerL }} />
              {weeks.map((_, w) => {
                const wi = wFrom + w
                const fin = engine.saldoFinAt(row.leafKey, wi)
                const red = engine.isRed(row.leafKey, wi)
                return (
                  <div
                    key={w}
                    onClick={() => onSelectWeek(wi, row.leafKey)}
                    className={clsx(cellWeekCls(w), 'cursor-pointer', red && (dark ? 'bg-red-500/15' : 'bg-red-50'))}
                    style={{ width: COL_W }}
                    title={matCellTip(row.leafKey, wi)}
                  >
                    <span className={clsx('text-[10px]', dispColor(row.leafKey, wi))}>
                      {fmtMiles(fin)}
                    </span>
                  </div>
                )
              })}
              <div className="shrink-0" style={{ width: spacerR }} />
            </div>
          )
        })}
        <div style={{ height: (rows.length - rTo) * rowH }} />
      </div>
    </div>
  )
}
